import React from "react";
import { AppStatus, AppStatusChoice } from "@/types/borrower";
import { BADGE_CONFIG, getStatusIcon } from "@/components/layout/DealStatusBadge";
import { useAppStatusTransitions } from "@/hooks/queries";
import { cn } from "@/lib/utils";

interface AppStatusPillProps {
  status: AppStatus;
  className?: string;
  /**
   * Pre-fetched status choices (label lookup). Pass this down from a parent
   * that already called `useAppStatusTransitions()` once (e.g. a board/table
   * rendering many pills) to avoid one `useQuery` subscription per pill —
   * falls back to fetching it itself when omitted.
   */
  choices?: AppStatusChoice[];
}

/**
 * Inline status pill used across list/board views: mini status icon + the
 * backend status label, in a tinted rounded pill. Kept as a single component
 * so every surface renders statuses identically.
 */
const AppStatusPill: React.FC<AppStatusPillProps> = ({ status, className, choices }) => {
  const { choices: fetchedChoices } = useAppStatusTransitions({ enabled: choices == null });
  const effectiveChoices = choices ?? fetchedChoices;
  const color = BADGE_CONFIG[status]?.color ?? "#64748b";
  const label = effectiveChoices.find((c) => c.value === status)?.label || status;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide",
        className
      )}
      style={{
        backgroundColor: `${color}18`,
        color,
        border: `1px solid ${color}38`,
      }}
    >
      {getStatusIcon(status, "h-3 w-3 shrink-0")}
      {label}
    </span>
  );
};

export default AppStatusPill;
